'use strict';

// ---------------------------------------------------------------------------
// Minimal sound system built on the Web Audio API.
//
// To swap a sound in the future: either overwrite the .wav file at the same
// path in public/sounds/, or change the path here in MANIFEST - nothing
// else in the game needs to change, it only ever refers to sounds by key
// (e.g. SoundManager.play('shoot')).
// ---------------------------------------------------------------------------
const SoundManager = (() => {
  const MANIFEST = {
    menuLoop: 'sounds/menu-loop.wav',
    gameLoop: 'sounds/game-loop.wav',
    shoot: 'sounds/shoot.wav',
    impact: 'sounds/impact.wav',
    proximity: 'sounds/proximity.wav',
    kill: 'sounds/kill.wav',
    gameOver: 'sounds/gameover.wav',
  };

  let ctx = null;
  let masterGain = null;
  // sfxGain feeds one-shot sounds (shoot/impact/kill/proximity/gameOver),
  // musicGain feeds the looping tracks (menuLoop/gameLoop) - both multiply
  // into masterGain, so the standalone mute button (master) always wins
  // regardless of the two finer Settings-screen toggles.
  let sfxGain = null;
  let musicGain = null;
  const buffers = {};
  const activeLoops = {};
  let muted = false;
  try { muted = localStorage.getItem('panzer_muted') === '1'; } catch (e) {}
  let sfxEnabled = true;
  try { sfxEnabled = localStorage.getItem('panzer_sfx_enabled') !== '0'; } catch (e) {}
  let musicEnabled = true;
  try { musicEnabled = localStorage.getItem('panzer_music_enabled') !== '0'; } catch (e) {}

  function ensureCtx() {
    if (ctx) return ctx;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    ctx = new Ctx();
    masterGain = ctx.createGain();
    masterGain.gain.value = muted ? 0 : 1;
    masterGain.connect(ctx.destination);

    sfxGain = ctx.createGain();
    sfxGain.gain.value = sfxEnabled ? 1 : 0;
    sfxGain.connect(masterGain);

    musicGain = ctx.createGain();
    musicGain.gain.value = musicEnabled ? 1 : 0;
    musicGain.connect(masterGain);

    return ctx;
  }

  // Fetches+decodes just the given manifest keys, so the caller can load the
  // one sound it needs right now (e.g. the menu loop) without waiting on the
  // rest of the manifest.
  function loadKeys(keys) {
    if (!ensureCtx()) return Promise.resolve();
    return Promise.all(
      keys.map((key) =>
        fetch(MANIFEST[key])
          .then((r) => r.arrayBuffer())
          .then((data) => new Promise((resolve, reject) => {
            // decodeAudioData has both promise and legacy callback forms;
            // the callback form is used here for maximum browser support.
            ctx.decodeAudioData(data, (buf) => { buffers[key] = buf; resolve(); }, reject);
          }))
          .catch((err) => console.warn('[sound] failed to load', key, err))
      )
    );
  }

  function loadAll() {
    return loadKeys(Object.keys(MANIFEST));
  }

  function unlock() {
    const c = ensureCtx();
    if (c && c.state === 'suspended') c.resume();
  }

  function play(key, { volume = 1, rate = 1 } = {}) {
    if (!ctx || !buffers[key]) return;
    const src = ctx.createBufferSource();
    src.buffer = buffers[key];
    src.playbackRate.value = rate;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(sfxGain);
    src.start();
  }

  function playLoop(key, { volume = 1 } = {}) {
    if (!ctx || !buffers[key]) return;
    stopLoop(key); // tear down any existing instance (incl. one mid ramp-down) first
    const src = ctx.createBufferSource();
    src.buffer = buffers[key];
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = volume;
    src.connect(gain).connect(musicGain);
    src.start();
    activeLoops[key] = { src, gain };
  }

  function stopLoop(key) {
    const l = activeLoops[key];
    if (!l) return;
    try { l.src.stop(); } catch (e) {}
    delete activeLoops[key];
  }

  // Ramps a playing loop's pitch and volume down to silence over `duration`
  // seconds (like a record player winding down), then stops it - a
  // game-over style effect instead of an abrupt cut.
  function slowDownAndStop(key, duration = 1.3) {
    const l = activeLoops[key];
    if (!l || !ctx) return;
    const now = ctx.currentTime;
    try {
      l.src.playbackRate.cancelScheduledValues(now);
      l.src.playbackRate.setValueAtTime(l.src.playbackRate.value, now);
      l.src.playbackRate.exponentialRampToValueAtTime(0.06, now + duration);
    } catch (e) {}
    try {
      l.gain.gain.cancelScheduledValues(now);
      l.gain.gain.setValueAtTime(l.gain.gain.value, now);
      l.gain.gain.linearRampToValueAtTime(0, now + duration);
    } catch (e) {}
    setTimeout(() => stopLoop(key), duration * 1000 + 80);
  }

  function setMuted(v) {
    muted = v;
    try { localStorage.setItem('panzer_muted', v ? '1' : '0'); } catch (e) {}
    if (masterGain) masterGain.gain.value = v ? 0 : 1;
  }
  function isMuted() { return muted; }
  function toggleMuted() { setMuted(!muted); return muted; }

  function setSfxEnabled(v) {
    sfxEnabled = v;
    try { localStorage.setItem('panzer_sfx_enabled', v ? '1' : '0'); } catch (e) {}
    if (sfxGain) sfxGain.gain.value = v ? 1 : 0;
  }
  function isSfxEnabled() { return sfxEnabled; }

  function setMusicEnabled(v) {
    musicEnabled = v;
    try { localStorage.setItem('panzer_music_enabled', v ? '1' : '0'); } catch (e) {}
    if (musicGain) musicGain.gain.value = v ? 1 : 0;
  }
  function isMusicEnabled() { return musicEnabled; }

  // Suspend/resume the whole context when the tab/app goes to the background
  // (e.g. switching apps on mobile) so looping music doesn't keep playing
  // while the page is hidden. Suspending stops the audio clock entirely,
  // so loops pick back up exactly where they left off on return.
  document.addEventListener('visibilitychange', () => {
    if (!ctx) return;
    if (document.hidden) {
      if (ctx.state === 'running') ctx.suspend();
    } else if (ctx.state === 'suspended') {
      ctx.resume();
    }
  });

  return {
    MANIFEST, loadAll, loadKeys, unlock, play, playLoop, stopLoop, slowDownAndStop,
    setMuted, isMuted, toggleMuted,
    setSfxEnabled, isSfxEnabled, setMusicEnabled, isMusicEnabled,
  };
})();
